import React, { useState } from 'react';
import {
  Box,
  Typography,
  Fade,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Link,
} from '@mui/material';
import {
  HelpCircle,
  ChevronDown,
  MessageCircle,
  Play,
  Smartphone,
  Shield,
  Building2,
  Globe,
  PhoneOff,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import trackEvent from '../utils/trackEvent';

const faqs = [
  {
    icon: <Smartphone size={18} strokeWidth={1.75} />,
    question: 'Meu SMS não está chegando',
    answer: (
      <>
        Desative o proxy do perfil e entre pelo <strong style={{ color: '#E5E7EB' }}>4G/5G</strong>.
        Tem proxys que a Meta marca para não enviar SMS.
      </>
    ),
  },
  {
    icon: <PhoneOff size={18} strokeWidth={1.75} />,
    question: 'Números indisponíveis no FTM',
    answer: (
      <>
        Aguarde uns 10 minutos e tente novamente. Frequentemente estamos fazendo reposição de novos números.
      </>
    ),
  },
  {
    icon: <Shield size={18} strokeWidth={1.75} />,
    question: 'Minha BM não verificou!',
    answer: (
      <>
        Caso tenha feito exatamente como na vídeo aula, basta chamar no suporte que iremos analisar o seu caso
        e possivelmente repor o seu token.
      </>
    ),
  },
  {
    icon: <Building2 size={18} strokeWidth={1.75} />,
    question: 'Posso usar o mesmo domínio e CNPJ para verificar mais BMs?',
    answer: (
      <>
        Recomendamos usar um único domínio por BM. CNPJ você pode até repetir, mas recomendamos no máximo umas 2 a 4 vezes.
        {' '}<strong style={{ color: '#F87171' }}>Jamais repita um telefone</strong> para receber SMS,
        senão sua BM ficará em análise infinita.
      </>
    ),
  },
  {
    icon: <Globe size={18} strokeWidth={1.75} />,
    question: 'Está com falha na propagação do meu domínio?',
    answer: (
      <>
        Certifique-se de que estejam apenas os apontamentos iguais aos da vídeo aula. Caso tenha mais apontamentos,
        apague todos e deixe apontando somente para o FTM. Confira no site{' '}
        <Link
          href="https://dnschecker.org/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: '#A78BFA', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          DNS Checker
        </Link>{' '}
        se todos os países estão propagados.
      </>
    ),
  },
];

export default function DuvidasFrequentes() {
  const [expanded, setExpanded] = useState(0);
  const navigate = useNavigate();

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 1, sm: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <HelpCircle size={28} strokeWidth={1.75} color="#7C3AED" />
          <Typography variant="h4" sx={{ color: '#fff' }}>Dúvidas Frequentes</Typography>
        </Box>
        <Typography sx={{ color: '#9CA3AF', mb: 4 }}>
          Respostas rápidas para os problemas mais comuns. Se ainda precisar de ajuda, chame o suporte.
        </Typography>

        {faqs.map((faq, i) => (
          <Accordion
            key={faq.question}
            expanded={expanded === i}
            onChange={() => setExpanded(expanded === i ? false : i)}
            sx={{
              background: '#141926',
              border: '1px solid rgba(255,255,255,0.06)',
              mb: 1.5,
              borderRadius: '12px !important',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                borderColor: 'rgba(124, 58, 237, 0.35)',
                background: 'rgba(124,58,237,0.05)',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ChevronDown size={20} strokeWidth={2} color="#A78BFA" />}
              sx={{ px: { xs: 2, sm: 3 }, py: 0.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 1 }}>
                <Box sx={{
                  width: 34,
                  height: 34,
                  minWidth: 34,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(124,58,237,0.15)',
                  color: '#A78BFA',
                }}>
                  {faq.icon}
                </Box>
                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: { xs: '0.92rem', sm: '1rem' } }}>
                  {faq.question}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pt: 0, pb: 2.5 }}>
              <Typography sx={{ color: '#9CA3AF', lineHeight: 1.75, fontSize: { xs: '0.9rem', sm: '0.95rem' }, pl: { sm: 6.25 } }}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}

        <Box sx={{
          mt: 3,
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          background: 'rgba(124,58,237,0.06)',
          border: '1px solid rgba(124,58,237,0.2)',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
              Ainda com dúvida?
            </Typography>
            <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
              Revise a vídeo aula ou fale com o suporte no WhatsApp.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<Play size={16} strokeWidth={2} />}
              onClick={() => navigate('/video-aulas')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: 'rgba(124,58,237,0.4)',
                color: '#A78BFA',
                '&:hover': { borderColor: '#7C3AED', background: 'rgba(124,58,237,0.1)' },
              }}
            >
              Vídeo Aulas
            </Button>
            <Button
              variant="contained"
              startIcon={<MessageCircle size={16} strokeWidth={2} />}
              href="https://wa.me/5567933005660"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('Contact', { contentName: 'WhatsApp FAQ' })}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                background: '#25D366',
                '&:hover': { background: '#1EBE57' },
              }}
            >
              Chamar suporte
            </Button>
          </Box>
        </Box>
      </Box>
    </Fade>
  );
}
